import { FormEvent } from "react"
import { Head, Link, useForm, usePage } from "@inertiajs/react"
import { FormattedMessage, useIntl } from "react-intl"
import { AuthShell } from "@/components/AuthShell"
import { GoogleButton, OrDivider } from "@/components/GoogleButton"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import type { PageProps } from "@/types/inertia"

export default function Login() {
  const intl = useIntl()
  const { props } = usePage<PageProps<{ google_enabled?: boolean }>>()
  const form = useForm({ email: "", password: "" })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.post("/login")
  }

  const baseError = props.errors?.base
  const title = intl.formatMessage({ id: "auth.login.title" })
  const description = intl.formatMessage({ id: "auth.login.description" })

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AuthShell betaNote>
        <div className="text-center">
          <h2 className="text-2xl">{title}</h2>
          <p className="mt-2 text-sm text-ink-muted">
            <FormattedMessage
              id="auth.login.no_account"
              values={{ link: (chunks) => <Link href="/signup">{chunks}</Link> }}
            />
          </p>
        </div>

        {baseError && (
          <p className="mt-4 text-center text-sm text-danger-display">{baseError}</p>
        )}

        {props.google_enabled && (
          <div className="mt-6">
            <GoogleButton />
            <OrDivider />
          </div>
        )}

        <form onSubmit={submit} className={props.google_enabled ? "space-y-4" : "mt-6 space-y-4"}>
          <div className="space-y-2">
            <label htmlFor="email">{intl.formatMessage({ id: "common.email" })}</label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={form.data.email}
              onChange={(e) => form.setData("email", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="password">{intl.formatMessage({ id: "common.password" })}</label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={form.data.password}
              onChange={(e) => form.setData("password", e.target.value)}
            />
          </div>
          <div className="flex flex-col items-center gap-3 pt-2 sm:flex-row sm:justify-between">
            <Button type="submit" disabled={form.processing}>
              {intl.formatMessage({ id: "auth.login.submit" })}
            </Button>
            <Link href="/passwords/new" className="text-sm">
              {intl.formatMessage({ id: "auth.login.forgot_password" })}
            </Link>
          </div>
        </form>
      </AuthShell>
    </>
  )
}
